import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from 'ant-design-vue';
import lodash from 'lodash';
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from '../../../../../app.logic';
import {
    createFieldInputNumber,
    createFieldSelect
} from "../../../../../components/input/template-builder/template-builder.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import {
    createColumnInputNumber,
    createColumnSelect
} from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store/index";
import { createButtonActive, createButtonArchive, createButtonCreate, createButtonDelete, createButtonEdit, createButtonSave, createButtonView } from "../../../../../utils/button";
import { modalStyleIFrame } from "../../../../../utils/constant";
import { checkAttribute, initUseForm, onClickActive, onClickActiveAll, onClickArchive, onClickArchiveAll, onClickCreate, onClickDelete, onClickDeleteAll, onClickEdit, onClickView } from '../../../../../utils/form';
import { createLoading } from '../../../../../utils/loading';
import { has } from "../../../../../utils/menu-context";
import { createAppPageHeaderTop, createManageActive, createManageArchive, createManageDelete, createManageExportCsv, createManageExtraCreate, createManageSelect, createPage, createRowActionEdit, createRowActionView } from '../../../../../utils/page';
import { required, validateError } from "../../../../../utils/validate";
import { getLabType, getLabTypeByKey, getLabTypeKeyByRoleMenuAttributes } from "../lab-share.logic";

const CIFrame = defineAsyncComponent(() => import('../../../../../components/iframe/CIFrame'))

export const { isLoading, setIsLoading } = createLoading()

export const actionPrefix = 'resource/v1/master/samplingMonitorTypeLabTopic'
export const routerPrefix = 'sampling-monitor-type-lab-topics'

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form })
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria })
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id })
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id })
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } })
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids })
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids })
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } })
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria })

export const callLabType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LAB_TYPE", value: searchQuery });
export const callSamplingMonitorType = ({ searchQuery }) => store.dispatch('resource/v1/master/samplingMonitorType/cacheFindAll', { searchQuery })
export const callLabTopic= ({ searchQuery, labTypeKey }) => store.dispatch('resource/v1/master/labTopic/cacheFindAll', { searchQuery, labTypeKey })

export const onSearch = ({ page, props }) => {
    setIsLoading(true);
    const where = page.where || [];
    const sort = page.sort || [];
    const whereLabType = [];
    const keys = getLabTypeKeyByRoleMenuAttributes({ props });
    if (keys.length > 0) {
        whereLabType.push({ openBucket: true, name: "refLabType.key", operator: "in", type: "String", value: keys });
        whereLabType.push({
            closeBucket: true,
            logic: "AND",
            name: "refLabType.group",
            operator: "=",
            type: "String",
            value: "LAB_TYPE"
        });
    }
    const whereAll =  [...where, ...whereLabType];
    window.onSearch?.({ page, whereAll });
    return callSearch({
        criteria: {
            ...page,
            select: ['**'],
            data: [],
            where: whereAll,
            sort: [...sort, { name: 'seq', type: 'ASC' }]
        }
    }).then(page => state.page = { ...page, where, sort })
        .finally(() => setIsLoading(false));
}

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true)
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then((record) => onClickView({ record, routerPrefix }))
        })
        .catch((error) => validateError({ error }));
}

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch((error) => {
            validateError({ error })
            throw error
        });
}

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ['**'],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
        }
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
            children: [],
        }
    }
}

export const createColumns = ({data}) => {
    const columns = [
        createColumnInputNumber({ title: () => i18n.global.t("model.samplingMonitorType.seq"), dataIndex: "seq", search: { visible: () => false } }),
        createColumnSelect({
            title: () => i18n.global.t("model.lookup.modelGroup.LAB_TYPE"),
            customRender: ({ record }) => <div>{record.refLabType?.value}</div>,
            dataIndex: "refLabType.value",
            inputProp: ({ field }) => {
                const labTopic = field.columns.find(d => d.dataIndex === 'refLabTopic.id');
                return {
                    visible: () => data.labAttributes.length == 1,
                    list: ({ searchQuery }) => getLabType({ searchQuery }).then(arrays => {
                        return arrays.filter(item => data.labAttributes.includes(item?.key));
                    }),
                    'onUpdate:value': (value) => {
                        field.searchValue = value;
                        data.searchLabType = value;
                        if (!value) {
                            labTopic.search.field.searchValue = null
                        }
                    },
                    optLabel: 'value'
                }
            },
            search: {
                name: "refLabType.id",
                field: {
                    searchValue: data.searchLabType,
                    rulesRef: [required]
                }
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingMonitorTypeLabTopic.refSamplingMonitorType"),
            customRender: ({ record }) => <div>{record.refSamplingMonitorType?.name}</div>,
            dataIndex: "refSamplingMonitorType.value",
            inputProp: ({ field }) => {
                return {
                    list: callSamplingMonitorType,
                    'onUpdate:value': (value) => {
                        field.searchValue = value;
                    },
                    optLabel: 'name'
                }
            },
            search: { name: "refSamplingMonitorType.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingMonitorTypeLabTopic.refLabTopic"),
            customRender: ({ record }) => <div>{record.refLabTopic?.name}</div>,
            dataIndex: "refLabTopic.value",
            inputProp: ({ field }) => {
                const labType = field.columns.find(d => d.dataIndex === 'refLabType.id')?.search?.field?.searchValue
                return {
                    list: ({ searchQuery }) => callLabTopic({searchQuery, labTypeKey: labType.key }),
                    'onUpdate:value': (value) => {
                        field.searchValue = value;
                    },
                    optLabel: 'name',
                    disabled: !labType
                }
            },
            search: { name: "refLabTopic.id" }
        }),
    ]
    return columns
}

export const createForm = () => {
    return {
        seq: null,
        refSamplingMonitorType: null,
        refLabTopic: null,
        refLabType: null,
    }
}

export const createRules = () => {
    return {
        seq: [required],
        refSamplingMonitorType: [required],
        refLabTopic: [required],
        refLabType: [required],
    }
}

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly
    const useForm = data.useForm
    const modelRef = useForm.modelRef
    return [
        createFieldInputNumber({
            key: 'seq',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.samplingMonitorType.seq'),
            inputProp: () => ({ value: modelRef.seq, 'onUpdate:value': value => modelRef.seq = value, disabled: viewOnly })
        }),
        createFieldSelect({
            key: 'refLabType',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.samplingMonitorTypeLabTopic.refLabType'),
            inputProp: () => ({
                value: modelRef.refLabType,
                'onUpdate:value': value => {
                    if (!value) {
                        modelRef.refLabTopic = null;
                    }
                    modelRef.refLabType = value
                },
                list: ({ searchQuery }) => getLabType({ searchQuery }).then(arrays => {
                    return arrays.filter(item => data?.labAttributes?.includes(item?.key));
                }),
                optLabel: "value",
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldSelect({
            key: 'refSamplingMonitorType',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.samplingMonitorTypeLabTopic.refSamplingMonitorType'),
            inputProp: () => ({
                value: modelRef.refSamplingMonitorType,
                'onUpdate:value': value => modelRef.refSamplingMonitorType = value,
                list: callSamplingMonitorType,
                optLabel: "name",
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.name`) }
        }),
        createFieldSelect({
            key: 'refLabTopic',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.samplingMonitorTypeLabTopic.refLabTopic'),
            inputProp: () => ({
                value: modelRef.refLabTopic,
                'onUpdate:value': value => modelRef.refLabTopic = value,
                list: ({ searchQuery }) => callLabTopic({ searchQuery, labTypeKey: modelRef?.refLabType?.key }),
                optLabel: "name",
                disabled: !modelRef?.refLabType
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.name`) }
        }),
    ]
}

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.name : i18n.global.t('button.create')
}

export const loadForm = ({ props, data }) => {
    setIsLoading(true)
    const { form } = props
    const { id, useForm } = data
    const { modelRef } = useForm
    let promise = null
    if (form) {
        promise = Promise.resolve({ data: [form] })
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) })
    } else {
        promise = Promise.resolve({ data: [modelRef] })
    }
    return promise
        .then(page => initUseForm({ data, page, routerPrefix }))
        .finally(() => setIsLoading(false))
}

export const initState = ({ props, data }) => {
    const menuContext = props.menuContext;
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    getLabTypeByKey({searchQuery: data.labAttributes[0]}).then(res => {
        data.searchLabType = res[0]
        if (state.columns.length == 0) {
            state.columns = createColumns({props, data});
        }
        state.manages = [
            createManageSelect({ menuContext }),
            createManageActive({
                menuContext,
                onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch, setIsLoading }),
            }),
            createManageArchive({
                menuContext,
                onClick: ({ rowSelection }) => onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch, setIsLoading }),
            }),
            createManageDelete({
                menuContext,
                onClick: ({ rowSelection }) => onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch, setIsLoading }),
            }),
            createManageExportCsv({ menuContext }),
        ]
        state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })]
        state.rowActions = [
            createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
            createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) }),
        ]
    });
}

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs })
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs })
        .then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                        default: () => createAppPageHeaderDefault({ props, data, refs })
                    }
                });
            }
        })
}

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = []
    const col2 = []
    const { menuContext, viewOnly } = props
    const { id, isActive } = data.useForm.modelRef
    if (id && has({ menuContext, key: 'canCreate' })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && has({ menuContext, key: 'canView' })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && isActive && has({ menuContext, key: 'canEdit' })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: 'canActive' })) {
        col2.push(createButtonActive({ onClick: () => onClickActive({ record: { id }, callActive, setIsLoading: setIsLoadingApp, onClickEdit: () => onClickEdit({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canArchive' })) {
        col2.push(createButtonArchive({ onClick: () => onClickArchive({ record: { id }, callArchive, setIsLoading: setIsLoadingApp, onClickView: () => onClickView({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canDelete' })) {
        col2.push(createButtonDelete({ onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }), classStyle: 'm-1' }))
    }
    return <Row type="flex">
        <Col>{col1}</Col>
        <Col class="ml-auto">{col2}</Col>
    </Row>
}

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => <CIFrame src={`${process.env.VUE_APP_PUBLIC_PATH}/app/master-mgt/sampling-monitor-types?isLayout=false&canSelect=true&cacheQuery=true`} onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}></CIFrame>
    })
    return options
}

export const state = reactive({
    columns: [],
    page: createPage({ options: { select: ['*'] } }),
    tableOptions: { search: { activeKey: 'Search' } }
})
